import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGlobalLoader } from "./GlobalLoader";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clipboard,
  ClipboardCheck,
  Database,
  Download,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  User,
  Users,
  X,
} from "lucide-react";
import axios from "axios";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { DialogClose } from "@radix-ui/react-dialog";
import {
  formatarData,
  converterListaZplItemParaUrl,
} from "@/utils/UtilFuncoes";
import { alert } from "@/contexts/AlertModalContext";
import impressao_socket_api from "@/services/impressao_socket_api";
import inovaone_api from "@/services/inovaone_api";
import type { ImpressaoItem } from "@/types/impressao-item";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useItem } from "@/contexts/ItemContext";
import { useLeitores } from "@/contexts/LeitoresContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useAuth } from "@/hooks/useAuth";

type LogsImpressaoComponentContextType = {};

const LogsImpressaoComponentContext = createContext<
  LogsImpressaoComponentContextType | undefined
>(undefined);

export function LogsImpressaoComponentProvider() {
  return (
    <LogsImpressaoComponentContext.Provider value={{}}>
      <LogsImpressaoComponent />
    </LogsImpressaoComponentContext.Provider>
  );
}

export function useLogsImpressaoComponent() {
  const context = useContext(LogsImpressaoComponentContext);
  if (!context) {
    throw new Error(
      "useLogsImpressaoComponent deve ser usado dentro de LogsImpressaoComponentProvider",
    );
  }
  return context;
}

type RetornoEPCNaBase = {
  epc: string;
  estado: string;
  na_base: boolean;
};

type LogConexaoSocket = {
  _id: string;
  hardware_id: string;
  pode_enviar_cabecalho: boolean;
  tipo_da_conexao: string;
  id_conexao: string;
  createdAt: string;
  updatedAt: string;
};

type LogErroSocket = {
  _id: string;
  hardware_id: string;
  nome: string;
  epc_relacionado: string;
  nome_da_ordem: string;
  descricao: string;
  createdAt: string;
  updatedAt: string;
};

type LogImpressaoEtiquetaTotal = {
  _id: string;
  codigo: string;
  descricao: string;
  epc: string;
  inf_compl_1: string;
  inf_compl_2: string;
  inf_compl_3: string;
  inf_compl_4: string;
  inf_compl_5: string;
  inf_compl_6: string;
  inf_compl_7: string;
  inf_compl_8: string;
  inf_compl_9: string;
  inf_compl_10: string;
  inf_compl_11: string;
  inf_compl_12: string;
  localizacao_1: string;
  localizacao_2: string;
  localizacao_3: string;
  localizacao_4: string;
  zpl: string;
  dt_validade: string;
  quantidade: string;
  valor: number;
  n_impressao?: string;
};

type LogImpressaoSocket = {
  nome_da_ordem: string;
  estado_da_ordem: string;
  observacao?: string;
  // Modo = tela/fluxo que originou a ordem; canal = por onde o ZPL saiu.
  // Só existem em ordens gravadas depois da versão que passou a registrá-los.
  modo_impressao?: string;
  detalhe_modo_impressao?: string;
  canal_impressao?: string;
  hardware_id: string;
  id_conta: string;
  id_usuario: string;
  etiquetas_impressas: string[];
  etiquetas_gravadas: string[];
  etiquetas_salvas: string[];
  etiquetas_total: LogImpressaoEtiquetaTotal[];
  data_criacao: string;
  data_ultima_atualizacao: string;
  erros: string[];
};

type StatusVariant = "success" | "info" | "warning" | "destructive" | "muted";

const statusBadgeClasses: Record<StatusVariant, string> = {
  success:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  info: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
  warning:
    "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  destructive: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
  muted: "bg-muted text-muted-foreground border-border",
};

function getStatusVariant(estado: string | undefined): StatusVariant {
  if (!estado) return "muted";
  if (estado === "Finalizado") return "success";
  if (estado === "Imprimindo") return "info";
  if (estado.toLowerCase().includes("erro")) return "destructive";
  return "warning";
}

function getStatusText(estado: string | undefined) {
  if (!estado) return "Desconhecido";
  if (estado === "Imprimindo") return "Em Andamento";
  return estado;
}

function getColumnHeadersByTab(tab: string) {
  switch (tab) {
    case "errors":
      return ["Hardware", "Descrição", "Ordem", "Data/Hora"];
    case "conexoes":
      return [
        "Hardware",
        "Tipo de Conexão",
        "ID da Conexão",
        "Data/Hora",
        "Estado",
      ];
    case "ordens":
    default:
      return [
        "Hardware",
        "Ordem",
        "Modo",
        "Última Atualização",
        "Estado",
        "Na Base",
      ];
  }
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={`sk-${i}`}>
          {Array.from({ length: cols }).map((_, j) => (
            <TableCell key={`sk-${i}-${j}`}>
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyRow({ cols, message }: { cols: number; message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className="h-32 text-center">
        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Inbox className="h-6 w-6 opacity-50" />
          <p className="text-sm">{message}</p>
        </div>
      </TableCell>
    </TableRow>
  );
}

// Gera na InovaOne um registro "rotas-conf" com as etiquetas da ordem, todas
// marcadas como ausentes. O registro leva o nome da ordem e aparece no app Rotas
// em Histórico > Conferência: ao ler cada etiqueta física, ela deixa de ser
// ausente — o que sobrar é o que não foi encontrado.
function BotaoInventarioConferencia({ log }: { log: LogImpressaoSocket }) {
  const { usuario } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [jaExiste, setJaExiste] = useState(false);

  const totalEtiquetas = useMemo(
    () => new Set(log.etiquetas_total.map((e) => e.epc).filter(Boolean)).size,
    [log.etiquetas_total],
  );

  const gerar = async (forcar: boolean) => {
    setGerando(true);
    try {
      const response = await impressao_socket_api.post(
        "/v1/inventario-conferencia",
        {
          hardware_id: log.hardware_id,
          nome_da_ordem: log.nome_da_ordem,
          id_conta: usuario?.id_conta,
          id_usuario: usuario?._id,
          forcar,
        },
      );

      // A ordem já tinha um inventário de conferência: não sobrescreve sem o
      // usuário confirmar, senão o que já foi conferido no app seria perdido.
      if (response.data.ja_processado && !forcar) {
        setJaExiste(true);
        return;
      }

      setAberto(false);
      setJaExiste(false);
      alert.success(
        `Inventário de conferência "${response.data.identificador}" gerado com ${response.data.qtd_etiquetas} etiqueta(s).`,
        {
          description:
            "Abra o app Rotas em Histórico > Conferência para conferir as etiquetas.",
        },
      );
    } catch (e: any) {
      console.log(e);
      alert.error(
        e?.response?.data?.message ||
          "Erro ao gerar o inventário de conferência.",
      );
    } finally {
      setGerando(false);
    }
  };

  return (
    <Dialog
      open={aberto}
      onOpenChange={(v) => {
        setAberto(v);
        if (!v) setJaExiste(false);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8"
          title="Gerar inventário de conferência"
          disabled={totalEtiquetas === 0}
        >
          <ClipboardCheck className="h-4 w-4 mr-1" />
          Conferência
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            Gerar inventário de conferência
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm">
            Cria um registro de conferência com o nome{" "}
            <span className="font-medium">{log.nome_da_ordem}</span> e as{" "}
            <span className="font-medium">
              {totalEtiquetas} etiqueta(s)
            </span>{" "}
            desta ordem marcadas como ausentes.
          </p>
          <p className="text-xs text-muted-foreground">
            No app Rotas, abra Histórico &gt; Conferência e toque no registro: ao
            ler cada etiqueta física, ela deixa de ser ausente. O que sobrar como
            ausente não foi encontrado.
          </p>

          {jaExiste && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
              <p className="text-xs text-amber-900 dark:text-amber-300">
                Esta ordem já tem um inventário de conferência. Gerar de novo
                zera o que já foi conferido no app.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="sm">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            size="sm"
            variant={jaExiste ? "destructive" : "default"}
            disabled={gerando}
            onClick={() => gerar(jaExiste)}
          >
            {gerando ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ClipboardCheck className="h-4 w-4 mr-2" />
            )}
            {jaExiste ? "Gerar novamente" : "Gerar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DialogModalDetalhesProps {
  log: LogImpressaoSocket;
  listaEpcsNaBase: RetornoEPCNaBase[];
  sincronizarOrdem: (ordem: LogImpressaoSocket, forcar?: boolean) => void;
  localizacoes: any[];
  coletaZplPadrao: () => any;
}

function DialogModalDetalhes({
  log,
  listaEpcsNaBase,
  sincronizarOrdem,
  localizacoes,
  coletaZplPadrao,
}: DialogModalDetalhesProps) {
  const [tabAtiva, setTabAtiva] = useState<"etiquetas" | "erros">("etiquetas");
  const variant = getStatusVariant(log.estado_da_ordem);

  return (
    <DialogContent className="sm:max-w-[720px] max-h-[85vh] p-0 flex flex-col gap-0">
      <DialogHeader className="px-6 py-4 border-b">
        <div className="flex items-center justify-between gap-3">
          <DialogTitle className="text-base">{log.nome_da_ordem}</DialogTitle>
          <Badge variant="outline" className={statusBadgeClasses[variant]}>
            {getStatusText(log.estado_da_ordem)}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Hardware:{" "}
          <span className="">{log.hardware_id}</span>
        </p>
        {(log.modo_impressao || log.canal_impressao) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {log.modo_impressao && (
              <Badge variant="outline" className="text-[11px] font-normal">
                {log.modo_impressao}
                {log.detalhe_modo_impressao
                  ? ` — ${log.detalhe_modo_impressao}`
                  : ""}
              </Badge>
            )}
            {log.canal_impressao && (
              <Badge variant="secondary" className="text-[11px] font-normal">
                {log.canal_impressao}
              </Badge>
            )}
          </div>
        )}
        {log.observacao && (
          <div className="mt-2 rounded-md border bg-muted/40 px-3 py-2">
            <p className="text-[11px] font-medium text-muted-foreground mb-0.5">
              Observação
            </p>
            <p className="text-xs whitespace-pre-line">{log.observacao}</p>
          </div>
        )}
      </DialogHeader>

      <div className="flex-1 flex flex-col overflow-hidden">
        <Tabs
          value={tabAtiva}
          onValueChange={(value) => setTabAtiva(value as "etiquetas" | "erros")}
          className="flex-1 flex flex-col px-6 pt-4"
        >
          <TabsList className="grid grid-cols-2 mb-4 w-full">
            <TabsTrigger value="etiquetas" className="text-xs">
              <CheckCircle className="h-3 w-3 mr-2" />
              Etiquetas
              <span className="ml-1 text-muted-foreground">
                ({log.etiquetas_total.length})
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="erros"
              className="text-xs"
              disabled={!log.erros || log.erros.length === 0}
            >
              <AlertTriangle className="h-3 w-3 mr-2" />
              Erros
              <span className="ml-1 text-muted-foreground">
                ({log.erros?.length || 0})
              </span>
            </TabsTrigger>
          </TabsList>

          {tabAtiva === "etiquetas" && (
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-4">
                  {log.etiquetas_total.length > 0 ? (
                    log.etiquetas_total.map((etiqueta, index) => {
                      const naBase = listaEpcsNaBase.some(
                        (e) => e.epc === etiqueta.epc && e.na_base,
                      );
                      return (
                        <Card
                          key={index}
                          className="p-3 bg-muted/30 hover:bg-muted/60 transition-colors"
                        >
                          <div className="space-y-2">
                            <div className="flex justify-between items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">
                                  {etiqueta.descricao}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {etiqueta.codigo}
                                </p>
                              </div>
                              <Badge
                                variant="outline"
                                className={
                                  statusBadgeClasses[
                                    naBase ? "success" : "destructive"
                                  ]
                                }
                              >
                                {naBase ? "Na Base" : "Não Encontrado"}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground break-all">
                              <span className="font-medium">EPC: </span>
                              {etiqueta.epc || "—"}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              <span className="font-medium">Local: </span>
                              {[
                                localizacoes.find(
                                  (l) =>
                                    l.descricao === etiqueta.localizacao_1 ||
                                    l._id === etiqueta.localizacao_1,
                                )?.descricao,
                                localizacoes
                                  .flatMap((l) => l.children)
                                  .find(
                                    (l2) =>
                                      l2.descricao === etiqueta.localizacao_2 ||
                                      l2._id === etiqueta.localizacao_2,
                                  )?.descricao,
                                localizacoes
                                  .flatMap((l) =>
                                    l.children.flatMap(
                                      (l2: any) => l2.children,
                                    ),
                                  )
                                  .find(
                                    (l3) =>
                                      l3.descricao === etiqueta.localizacao_3 ||
                                      l3._id === etiqueta.localizacao_3,
                                  )?.descricao,
                                localizacoes
                                  .flatMap((l) =>
                                    l.children.flatMap(
                                      (l2: any) => l2.children,
                                    ),
                                  )
                                  .flatMap((l3: any) => l3.children)
                                  .find(
                                    (l4) =>
                                      l4.descricao === etiqueta.localizacao_4 ||
                                      l4._id === etiqueta.localizacao_4,
                                  )?.descricao,
                              ]
                                .filter(Boolean)
                                .join(" > ") || "—"}
                            </p>
                            <div className="mt-2 pt-2 border-t">
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full h-7 text-xs"
                                onClick={() => {
                                  const zplPadrao = coletaZplPadrao();
                                  if (zplPadrao && etiqueta.zpl) {
                                    const itemZpl = {
                                      ...zplPadrao,
                                      zpl: etiqueta.zpl,
                                    };
                                    const url =
                                      converterListaZplItemParaUrl(itemZpl);
                                    window.open(url, "_blank");
                                  } else {
                                    alert.error(
                                      "Erro ao visualizar etiqueta. ZPL não configurado.",
                                    );
                                  }
                                }}
                              >
                                Visualizar Etiqueta
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma etiqueta encontrada</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {tabAtiva === "erros" && log.erros && log.erros.length > 0 && (
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-4">
                  {[...new Set(log.erros)].map((erro, index) => (
                    <Card
                      key={`${erro}-${index}`}
                      className="p-3 bg-red-500/5 hover:bg-red-500/10 transition-colors border-red-500/20"
                    >
                      <div className="flex gap-3">
                        <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-red-900 dark:text-red-300 break-words">
                            {erro}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </Tabs>
      </div>

      <DialogFooter className="px-6 py-4 border-t gap-2 sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => sincronizarOrdem(log)}
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Sincronizar
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => sincronizarOrdem(log, true)}
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Forçar Sincronização
          </Button>
          <BotaoInventarioConferencia log={log} />
        </div>
        <DialogClose asChild>
          <Button type="button" variant="outline" size="sm">
            Fechar
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}

function LogsImpressaoComponent() {
  const context = useContext(LogsImpressaoComponentContext);
  if (!context) return null;
  const { contaConfig } = useContaConfig();
  const { pesquisarItensNaBase, salvarItensNaBase } = useItem();
  const { categorias } = useCategorias();
  const { localizacoes } = useLocalizacoes();
  const { impressaoConfig, todosOsHardwaresIds, coletaZplPadrao } =
    useImpressaoConfig();
  const { leitores } = useLeitores();
  const { isLoading, setIsLoading } = useGlobalLoader();

  const [activeTab, setActiveTab] = useState<"ordens" | "errors" | "conexoes">(
    "ordens",
  );
  const [mostrarTodosHardwareDaConta, setMostrarTodosHardwareDaConta] =
    useState(false);
  const [itensPorPagina, setItensPorPagina] = useState("10");

  const [paginaLogsErros, setPaginaLogsErros] = useState(1);
  const [paginaLogsImpressao, setPaginaLogsImpressao] = useState(1);
  const [paginaLogsConexao, setPaginaLogsConexao] = useState(1);
  const [totalPaginasLogsErros, setTotalPaginasLogsErros] = useState(0);
  const [totalPaginasLogsImpressao, setTotalPaginasLogsImpressao] = useState(0);
  const [totalPaginasLogsConexao, setTotalPaginasLogsConexao] = useState(0);

  const [totalDeOrdens, setTotalDeOrdem] = useState(0);
  const [totalDeOrdensFinalizadas, setTotalDeOrdensFinalizadas] = useState(0);
  const [listaDeErros, setListaDeErros] = useState<string[]>([]);
  const [listaDeEstados, setListaDeEstados] = useState<string[]>([]);
  const [listaLogsConexao, setListaLogsConexao] = useState<LogConexaoSocket[]>(
    [],
  );
  const [listaLogsErros, setListaLogsErros] = useState<LogErroSocket[]>([]);
  const [listaLogsImpressao, setListaLogsImpressao] = useState<
    LogImpressaoSocket[]
  >([]);
  const [listaEpcsNaBase, setListaEpcsNaBase] = useState<RetornoEPCNaBase[]>(
    [],
  );

  const [filtroDeErros, setFiltroDeErros] = useState<string>("*");
  const [filtroDeEstados, setFiltroDeEstados] = useState<string>("*");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");
  const [horaInicio, setHoraInicio] = useState<string>("00:00:00");
  const [horaFim, setHoraFim] = useState<string>("23:59:59");
  const [filtroEpc, setFiltroEpc] = useState<string>("");
  const [debouncedFiltroEpc, setDebouncedFiltroEpc] = useState<string>("");

  const [loadingOrdens, setLoadingOrdens] = useState(false);
  const [loadingErros, setLoadingErros] = useState(false);
  const [loadingConexoes, setLoadingConexoes] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ---- Setters que resetam página ao mudar filtro ----
  const resetPages = useCallback(() => {
    setPaginaLogsImpressao(1);
    setPaginaLogsErros(1);
    setPaginaLogsConexao(1);
  }, []);

  const updateMostrarTodos = useCallback(
    (v: boolean) => {
      setMostrarTodosHardwareDaConta(v);
      resetPages();
    },
    [resetPages],
  );
  const updateDataInicio = (v: string) => {
    setDataInicio(v);
    if (!v) setHoraInicio("00:00:00");
    resetPages();
  };
  const updateDataFim = (v: string) => {
    setDataFim(v);
    if (!v) setHoraFim("23:59:59");
    resetPages();
  };
  const updateHoraInicio = (v: string) => {
    setHoraInicio(v);
    resetPages();
  };
  const updateHoraFim = (v: string) => {
    setHoraFim(v);
    resetPages();
  };
  const updateFiltroDeEstados = (v: string) => {
    setFiltroDeEstados(v);
    setPaginaLogsImpressao(1);
  };
  const updateFiltroDeErros = (v: string) => {
    setFiltroDeErros(v);
    setPaginaLogsErros(1);
  };
  const updateFiltroEpc = (v: string) => {
    setFiltroEpc(v);
  };
  const updateItensPorPagina = (v: string) => {
    setItensPorPagina(v);
    resetPages();
  };
  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setHoraInicio("00:00:00");
    setHoraFim("23:59:59");
    setFiltroDeErros("*");
    setFiltroDeEstados("*");
    setFiltroEpc("");
    setDebouncedFiltroEpc("");
    resetPages();
  };

  // Debounce do EPC + reset de página (batched dentro do setTimeout)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFiltroEpc(filtroEpc);
      setPaginaLogsImpressao(1);
    }, 400);
    return () => clearTimeout(t);
  }, [filtroEpc]);

  const temFiltrosAtivos = useMemo(
    () =>
      filtroDeErros !== "*" ||
      filtroDeEstados !== "*" ||
      !!dataInicio ||
      !!dataFim ||
      !!debouncedFiltroEpc,
    [filtroDeErros, filtroDeEstados, dataInicio, dataFim, debouncedFiltroEpc],
  );

  const dataParamsSimples = useMemo(() => {
    let q = "";
    if (dataInicio) q += `&data_inicio=${dataInicio}`;
    if (dataFim) q += `&data_fim=${dataFim}`;
    return q;
  }, [dataInicio, dataFim]);

  // ---- Listagens auxiliares (estados/erros disponíveis) ----
  const listarListaDeEstados = useCallback(async () => {
    if (!impressaoConfig?.app_id) return;
    try {
      if (mostrarTodosHardwareDaConta) {
        const response = await impressao_socket_api.get(
          `/v1/listaDeEstados-multipla?hardware_ids=${todosOsHardwaresIds.join(",")}`,
        );
        if (response.data) setListaDeEstados(response.data);
      } else {
        const response = await impressao_socket_api.get(
          `/v1/listaDeEstados/${encodeURIComponent(impressaoConfig.app_id)}`,
        );
        if (response.data) setListaDeEstados(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [
    impressaoConfig?.app_id,
    mostrarTodosHardwareDaConta,
    todosOsHardwaresIds,
  ]);

  const listarListaDeErros = useCallback(async () => {
    if (!impressaoConfig?.app_id) return;
    try {
      if (mostrarTodosHardwareDaConta) {
        const response = await impressao_socket_api.get(
          `/v1/listaDeErros-multipla?hardware_ids=${todosOsHardwaresIds.join(",")}`,
        );
        if (response.data) setListaDeErros(response.data);
      } else {
        const response = await impressao_socket_api.get(
          `/v1/listaDeErros/${encodeURIComponent(impressaoConfig.app_id)}`,
        );
        if (response.data) setListaDeErros(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [
    impressaoConfig?.app_id,
    mostrarTodosHardwareDaConta,
    todosOsHardwaresIds,
  ]);

  // ---- Fetches principais (um por aba) ----
  const listarLogsConexao = useCallback(async () => {
    if (!impressaoConfig?.app_id) return;
    setLoadingConexoes(true);
    try {
      const base = mostrarTodosHardwareDaConta
        ? `/v1/conexao-multipla?hardware_ids=${todosOsHardwaresIds.join(",")}`
        : `/v1/conexao/${encodeURIComponent(impressaoConfig.app_id)}?`;
      const sep = mostrarTodosHardwareDaConta ? "&" : "";
      const url = `${base}${sep}pagina=${paginaLogsConexao}&quantidade_por_pagina=${itensPorPagina}${dataParamsSimples}`;
      const response = await impressao_socket_api.get(url);
      if (response.data?.conexoes) {
        const lista: LogConexaoSocket[] = response.data.conexoes
          .map((log: LogConexaoSocket) => {
            const data = new Date(log.createdAt);
            const dataFormatada = data.toLocaleString("pt-BR", {
              timeZone: "America/Sao_Paulo",
            });
            return { ...log, data_hora: dataFormatada, data_criacao: data };
          })
          .sort(
            (a: any, b: any) =>
              b.data_criacao.getTime() - a.data_criacao.getTime(),
          );
        setListaLogsConexao(lista);
        setTotalPaginasLogsConexao(response.data.total_paginas);
      }
    } catch (e) {
      console.log(e);
      setListaLogsConexao([]);
    } finally {
      setLoadingConexoes(false);
    }
  }, [
    impressaoConfig?.app_id,
    mostrarTodosHardwareDaConta,
    todosOsHardwaresIds,
    paginaLogsConexao,
    itensPorPagina,
    dataParamsSimples,
  ]);

  const listarLogsErros = useCallback(async () => {
    if (!impressaoConfig?.app_id) return;
    setLoadingErros(true);
    try {
      const base = mostrarTodosHardwareDaConta
        ? `/v1/erro-multipla?hardware_ids=${todosOsHardwaresIds.join(",")}`
        : `/v1/erro/${encodeURIComponent(impressaoConfig.app_id)}?`;
      const sep = mostrarTodosHardwareDaConta ? "&" : "";
      const url = `${base}${sep}pagina=${paginaLogsErros}&quantidade_por_pagina=${itensPorPagina}&nome_erro=${filtroDeErros}${dataParamsSimples}`;
      const response = await impressao_socket_api.get(url);
      if (response.data?.erros) {
        const lista: LogErroSocket[] = response.data.erros
          .map((log: LogErroSocket) => {
            const data = new Date(log.createdAt);
            const dataFormatada = data.toLocaleString("pt-BR", {
              timeZone: "America/Sao_Paulo",
            });
            return { ...log, data_hora: dataFormatada, data_criacao: data };
          })
          .sort(
            (a: any, b: any) =>
              b.data_criacao.getTime() - a.data_criacao.getTime(),
          );
        setListaLogsErros(lista);
        setTotalPaginasLogsErros(response.data.total_paginas);
      }
    } catch (e) {
      console.log(e);
      alert.error("Erro ao buscar os logs de erro do socket");
      setListaLogsErros([]);
    } finally {
      setLoadingErros(false);
    }
  }, [
    impressaoConfig?.app_id,
    mostrarTodosHardwareDaConta,
    todosOsHardwaresIds,
    paginaLogsErros,
    itensPorPagina,
    filtroDeErros,
    dataParamsSimples,
  ]);

  const listarLogsImpressao = useCallback(async () => {
    if (!impressaoConfig?.app_id) return;
    setLoadingOrdens(true);
    try {
      // O endpoint single suporta hora_inicio/hora_fim e epc; o multipla, não.
      let dataParams = dataParamsSimples;
      if (!mostrarTodosHardwareDaConta) {
        if (dataInicio && horaInicio) dataParams += `&hora_inicio=${horaInicio}`;
        if (dataFim && horaFim) dataParams += `&hora_fim=${horaFim}`;
        if (debouncedFiltroEpc) dataParams += `&epc=${debouncedFiltroEpc}`;
      }

      const base = mostrarTodosHardwareDaConta
        ? `/v1/impressao-multipla?hardware_ids=${todosOsHardwaresIds.join(",")}`
        : `/v1/impressao/${encodeURIComponent(impressaoConfig.app_id)}?id_conta=${impressaoConfig.id_conta}`;
      const url = `${base}&pagina=${paginaLogsImpressao}&quantidade_por_pagina=${itensPorPagina}&estado=${filtroDeEstados}${dataParams}`;

      const response = await impressao_socket_api.get(url);
      if (response.data?.ordens) {
        const lista: LogImpressaoSocket[] = response.data.ordens
          .map((log: LogImpressaoSocket) => {
            const data = new Date(log.data_criacao);
            const dataFormatada = data.toLocaleString("pt-BR", {
              timeZone: "America/Sao_Paulo",
            });
            return { ...log, data_hora: dataFormatada, data_criacao: data };
          })
          .filter((log: LogImpressaoSocket) => {
            if (!dataInicio && !dataFim) return true;
            const du = new Date(log.data_ultima_atualizacao);
            const hi = mostrarTodosHardwareDaConta ? "00:00:00" : horaInicio;
            const hf = mostrarTodosHardwareDaConta ? "23:59:59" : horaFim;
            const di = dataInicio ? new Date(`${dataInicio}T${hi}`) : null;
            const df = dataFim ? new Date(`${dataFim}T${hf}`) : null;
            if (di && du < di) return false;
            if (df && du > df) return false;
            return true;
          })
          .sort(
            (a: any, b: any) =>
              b.data_criacao.getTime() - a.data_criacao.getTime(),
          );
        setListaLogsImpressao(lista);
        setTotalPaginasLogsImpressao(response.data.total_paginas);
        setTotalDeOrdem(response.data.total);
        setTotalDeOrdensFinalizadas(response.data.ordens_finalizadas);
      }
    } catch (e) {
      console.log(e);
      setListaLogsImpressao([]);
    } finally {
      setLoadingOrdens(false);
    }
  }, [
    impressaoConfig?.app_id,
    impressaoConfig?.id_conta,
    mostrarTodosHardwareDaConta,
    todosOsHardwaresIds,
    paginaLogsImpressao,
    itensPorPagina,
    filtroDeEstados,
    dataParamsSimples,
    dataInicio,
    dataFim,
    horaInicio,
    horaFim,
    debouncedFiltroEpc,
  ]);

  // ---- Effects: 1 para listas auxiliares + 1 por aba ----
  useEffect(() => {
    listarListaDeEstados();
    listarListaDeErros();
  }, [listarListaDeEstados, listarListaDeErros]);

  useEffect(() => {
    listarLogsImpressao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listarLogsImpressao, refreshKey]);

  useEffect(() => {
    listarLogsErros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listarLogsErros, refreshKey]);

  useEffect(() => {
    listarLogsConexao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listarLogsConexao, refreshKey]);

  // ---- Ações ----
  const verificarEpcsNaBase = async () => {
    setIsLoading(true);
    try {
      if (!impressaoConfig) return;
      const epcs: RetornoEPCNaBase[] = [];
      listaLogsImpressao.forEach((log) => {
        log.etiquetas_total.forEach((etiqueta) => {
          epcs.push({
            epc: etiqueta.epc,
            estado: log.estado_da_ordem,
            na_base: false,
          });
        });
      });

      let queryParams = "";
      if (dataInicio && dataInicio !== "-") {
        queryParams = `&data_inicial=${encodeURIComponent(dataInicio)}`;
        if (dataFim && dataFim !== "-") {
          queryParams += `&data_final=${encodeURIComponent(dataFim)}`;
        }
      }

      const response_epcs = await inovaone_api.post(
        `/pesquisar-itens/${impressaoConfig.id_conta}?${queryParams}`,
        [{ tags: epcs.map((epc) => epc.epc) }],
      );
      if (response_epcs.data) {
        const newListaEpcsNaBase: RetornoEPCNaBase[] = [];
        epcs.forEach((epc) => {
          if (!epc.epc) return;
          const newEpc = { ...epc };
          newEpc.na_base = response_epcs.data.some(
            (item: any) => item.tag === epc.epc,
          );
          const index = newListaEpcsNaBase.findIndex(
            (item) => item.epc === epc.epc,
          );
          if (index !== -1) {
            newListaEpcsNaBase[index] = newEpc;
          } else {
            newListaEpcsNaBase.push(newEpc);
          }
        });
        setListaEpcsNaBase(newListaEpcsNaBase);
      }
    } catch (e) {
      console.error(e);
      alert.error("Erro ao verificar os itens na base.");
    } finally {
      setIsLoading(false);
    }
  };

  // Trava contra clique repetido: cada clique em "Forçar Sincronização"
  // reenviava a ordem inteira ao Sisplan enquanto a anterior ainda rodava.
  const sincronizandoRef = useRef(false);

  const sincronizarOrdem = async (
    ordem: LogImpressaoSocket,
    forcar: boolean = false,
  ) => {
    if (sincronizandoRef.current) {
      alert.info("A sincronização desta ordem ainda está em andamento.");
      return;
    }
    sincronizandoRef.current = true;
    try {
      await sincronizarOrdemUmaVez(ordem, forcar);
    } finally {
      sincronizandoRef.current = false;
    }
  };

  const sincronizarOrdemUmaVez = async (
    ordem: LogImpressaoSocket,
    forcar: boolean,
  ) => {
    const isValidHexEpc = (epc: string): boolean => /^[A-Fa-f0-9]+$/.test(epc);

    const itens: ImpressaoItem[] = ordem.etiquetas_total
      .filter((etiqueta) => isValidHexEpc(etiqueta.epc))
      .map((etiqueta) => ({
        codigo: etiqueta.codigo,
        descricao: etiqueta.descricao,
        epc: etiqueta.epc,
        inf_compl_1: etiqueta.inf_compl_1,
        inf_compl_2: etiqueta.inf_compl_2,
        inf_compl_3: etiqueta.inf_compl_3,
        inf_compl_4: etiqueta.inf_compl_4,
        inf_compl_5: etiqueta.inf_compl_5,
        inf_compl_6: etiqueta.inf_compl_6,
        inf_compl_7: etiqueta.inf_compl_7,
        inf_compl_8: etiqueta.inf_compl_8,
        inf_compl_9: etiqueta.inf_compl_9,
        inf_compl_10: etiqueta.inf_compl_10,
        inf_compl_11: etiqueta.inf_compl_11,
        inf_compl_12: etiqueta.inf_compl_12,
        dt_validade: etiqueta.dt_validade,
        id_externo: "",
        id_categoria:
          categorias.find((c) => c.ean === etiqueta.codigo)?._id || "",
        id_conta: impressaoConfig?.id_conta || "",
        id_localizacao1:
          localizacoes.find(
            (l) =>
              l.descricao === etiqueta.localizacao_1 ||
              l._id === etiqueta.localizacao_1,
          )?._id || "",
        id_localizacao2:
          localizacoes
            .flatMap((l) => l.children)
            .find(
              (l2) =>
                l2.descricao === etiqueta.localizacao_2 ||
                l2._id === etiqueta.localizacao_2,
            )?._id || "",
        id_localizacao3:
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .find(
              (l3) =>
                l3.descricao === etiqueta.localizacao_3 ||
                l3._id === etiqueta.localizacao_3,
            )?._id || "",
        id_localizacao4:
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .flatMap((l3) => l3.children)
            .find(
              (l4) =>
                l4.descricao == etiqueta.localizacao_4 ||
                l4._id === etiqueta.localizacao_4,
            )?._id || "",
        id_usuario: impressaoConfig?.id_usuario || "",
        quantidade: etiqueta.quantidade,
        tag: etiqueta.epc,
        valor: etiqueta.valor,
        zpl_impressao: etiqueta.zpl,
        _id: etiqueta._id,
        n_impressao: etiqueta?.n_impressao || "",
        ultima_localizacao: {
          verificar: contaConfig?.transferencia_loc_origem || false,
          primeira_vez: true,
        },
      }));

    const totalEtiquetas = ordem.etiquetas_total.length;
    if (totalEtiquetas > itens.length) {
      console.log(
        `${totalEtiquetas - itens.length} item(s) removido(s) por EPC inválido`,
      );
    }

    const itensNaBase = await pesquisarItensNaBase(
      itens.map((i) => i.tag),
      dataInicio,
      dataFim,
    );

    const itensParaSincronizar: ImpressaoItem[] = forcar
      ? itens
      : itens.filter(
          (item) =>
            !itensNaBase.some(
              (i) => i.tag === item.tag && i.id_conta === item.id_conta,
            ),
        );

    if (itensParaSincronizar.length === 0) {
      alert.error("Nenhum item para sincronizar.");
      return;
    }

    if (
      itensParaSincronizar.some((i) => i.tag === "") ||
      itensParaSincronizar.some(
        (i) => i.id_localizacao1 === "" || i._id === "",
      )
    ) {
      alert.error("Esta ordem de impressão não pode ser sincronizada.");
      return;
    }

    try {
      await salvarItensNaBase(itensParaSincronizar, false, forcar);
    } catch {
      alert.error("Erro ao sincronizar a ordem de impressão.");
      return;
    }

    // Ao forçar sincronização, reenviamos a ordem para o webhook de ordem
    // finalizada (se configurado), que na Splash vincula os EPCs no Sisplan.
    // O retorno do webhook vai para a tela: antes só ia para o console e o
    // operador via "sincronizado com sucesso" mesmo com o Sisplan recusando.
    if (!(forcar && impressaoConfig?.webhook_ordem_finalizada)) {
      alert.success("Itens sincronizados com sucesso.");
      verificarEpcsNaBase();
      return;
    }

    try {
      const resposta = await axios.post(
        impressaoConfig.webhook_ordem_finalizada,
        {
          nome_da_ordem: ordem.nome_da_ordem,
          estado_da_ordem: ordem.estado_da_ordem,
          hardware_id: ordem.hardware_id,
          id_conta: ordem.id_conta,
          id_usuario: ordem.id_usuario,
          etiquetas_impressas: ordem.etiquetas_impressas,
          etiquetas_gravadas: ordem.etiquetas_gravadas,
          etiquetas_salvas: ordem.etiquetas_salvas,
          etiquetas_total: ordem.etiquetas_total,
          data_criacao: ordem.data_criacao,
          data_ultima_atualizacao: ordem.data_ultima_atualizacao,
        },
        { timeout: 180000 },
      );
      const enviados = resposta.data?.enviados;
      alert.success(
        typeof enviados === "number"
          ? `Itens sincronizados. ${enviados} EPC(s) enviados ao ERP.`
          : "Itens sincronizados e ordem reenviada ao ERP.",
      );
    } catch (error) {
      console.error("Erro ao enviar webhook de ordem finalizada:", error);
      const erroHttp = axios.isAxiosError(error) ? error : undefined;
      const mensagem =
        (erroHttp?.response?.data as { message?: string } | undefined)
          ?.message ||
        (erroHttp?.code === "ECONNABORTED"
          ? "O ERP não respondeu a tempo."
          : "Não foi possível falar com o ERP.");
      alert.error(`Itens sincronizados na base, mas o ERP não confirmou: ${mensagem}`);
    } finally {
      verificarEpcsNaBase();
    }
  };

  const exportarItensNaBase = () => {
    const etiquetasNaBase: Array<
      LogImpressaoEtiquetaTotal & {
        hardware_id: string;
        nome_da_ordem: string;
      }
    > = [];
    const epcsJaAdicionados = new Set<string>();
    listaLogsImpressao.forEach((log) => {
      log.etiquetas_total.forEach((etiqueta) => {
        const epc = etiqueta.epc;
        const epcNaBase = listaEpcsNaBase.find(
          (o) => o.epc === epc && o.na_base,
        );
        if (
          epc &&
          epc.trim() !== "" &&
          !epcsJaAdicionados.has(epc) &&
          epcNaBase
        ) {
          epcsJaAdicionados.add(epc);
          etiquetasNaBase.push({
            ...etiqueta,
            hardware_id: log.hardware_id,
            nome_da_ordem: log.nome_da_ordem,
          });
        }
      });
    });

    if (etiquetasNaBase.length === 0) {
      alert.error("Nenhum item na base encontrado para exportar.");
      return;
    }

    const csvHeaders = [
      "EPC",
      "CODIGO",
      "DESCRICAO",
      "LOCALIZACAO_1",
      "LOCALIZACAO_2",
      "LOCALIZACAO_3",
      "LOCALIZACAO_4",
      "QUANTIDADE",
      "VALOR",
      "DATA_VALIDADE",
      "INF_COMPL_1",
      "INF_COMPL_2",
      "INF_COMPL_3",
      "INF_COMPL_4",
      "INF_COMPL_5",
      "HARDWARE_ID",
      "DATA",
    ].join(",");

    const csvRows = etiquetasNaBase
      .filter((e) => listaEpcsNaBase.some((o) => o.epc === e.epc && o.na_base))
      .map((e) => {
        const nomeLoc1 =
          localizacoes.find(
            (l) =>
              l.descricao === e.localizacao_1 || l._id === e.localizacao_1,
          )?.descricao ||
          e.localizacao_1 ||
          "";
        const nomeLoc2 =
          localizacoes
            .flatMap((l) => l.children)
            .find(
              (l2) =>
                l2.descricao === e.localizacao_2 ||
                l2._id === e.localizacao_2,
            )?.descricao ||
          e.localizacao_2 ||
          "";
        const nomeLoc3 =
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .find(
              (l3) =>
                l3.descricao === e.localizacao_3 ||
                l3._id === e.localizacao_3,
            )?.descricao ||
          e.localizacao_3 ||
          "";
        const nomeLoc4 =
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .flatMap((l3) => l3.children)
            .find(
              (l4) =>
                l4.descricao === e.localizacao_4 ||
                l4._id === e.localizacao_4,
            )?.descricao ||
          e.localizacao_4 ||
          "";

        return [
          e.epc || "",
          e.codigo || "",
          `"${(e.descricao || "").replace(/"/g, '""')}"`,
          `"${nomeLoc1.replace(/"/g, '""')}"`,
          `"${nomeLoc2.replace(/"/g, '""')}"`,
          `"${nomeLoc3.replace(/"/g, '""')}"`,
          `"${nomeLoc4.replace(/"/g, '""')}"`,
          e.quantidade || "",
          e.valor || "",
          e.dt_validade || "",
          `"${(e.inf_compl_1 || "").replace(/"/g, '""')}"`,
          `"${(e.inf_compl_2 || "").replace(/"/g, '""')}"`,
          `"${(e.inf_compl_3 || "").replace(/"/g, '""')}"`,
          `"${(e.inf_compl_4 || "").replace(/"/g, '""')}"`,
          `"${(e.inf_compl_5 || "").replace(/"/g, '""')}"`,
          e.hardware_id || "",
          `"${(() => {
            const log = listaLogsImpressao.find((l) =>
              l.etiquetas_total.some((et) => et.epc === e.epc),
            );
            if (log && log.data_criacao) {
              const data = new Date(log.data_criacao);
              const dia = String(data.getDate()).padStart(2, "0");
              const mes = String(data.getMonth() + 1).padStart(2, "0");
              const ano = String(data.getFullYear()).slice(-2);
              return `${dia}/${mes}/${ano}`;
            }
            return "";
          })()}"`,
        ].join(",");
      });

    const csvContent = [csvHeaders, ...csvRows].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `itens_na_base_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    alert.success(`${csvRows.length} itens na base exportados com sucesso!`);
  };

  // ---- Derivados ----
  const pagina =
    activeTab === "ordens"
      ? paginaLogsImpressao
      : activeTab === "errors"
        ? paginaLogsErros
        : paginaLogsConexao;
  const totalPagina =
    activeTab === "ordens"
      ? totalPaginasLogsImpressao
      : activeTab === "errors"
        ? totalPaginasLogsErros
        : totalPaginasLogsConexao;
  const isLoadingActiveTab =
    activeTab === "ordens"
      ? loadingOrdens
      : activeTab === "errors"
        ? loadingErros
        : loadingConexoes;

  const setPaginaActiveTab = (p: number) => {
    if (activeTab === "ordens") setPaginaLogsImpressao(p);
    if (activeTab === "errors") setPaginaLogsErros(p);
    if (activeTab === "conexoes") setPaginaLogsConexao(p);
  };
  const irPara = (p: number) => {
    if (p < 1 || p > Math.max(1, totalPagina)) return;
    setPaginaActiveTab(p);
  };

  const itensNaBaseUnicos = useMemo(() => {
    const todosEpcs = [
      ...new Set(
        listaLogsImpressao.flatMap((log) =>
          log.etiquetas_total
            .map((e) => e.epc)
            .filter((epc) => epc && epc.trim() !== ""),
        ),
      ),
    ];
    return todosEpcs.filter((epc) =>
      listaEpcsNaBase.some((o) => o.epc === epc && o.na_base),
    ).length;
  }, [listaLogsImpressao, listaEpcsNaBase]);

  const registrosNaTab =
    activeTab === "ordens"
      ? listaLogsImpressao.length
      : activeTab === "errors"
        ? listaLogsErros.length
        : listaLogsConexao.length;

  const nomeLeitor = (serial: string) => {
    const l = leitores.find((x) => x.serial === serial);
    return l?.nm_leitor ? ` · ${l.nm_leitor}` : "";
  };

  const paginationItems = useMemo(() => {
    const items: (number | "ellipsis")[] = [];
    const total = Math.max(1, totalPagina);
    if (total <= 7) {
      for (let i = 1; i <= total; i++) items.push(i);
    } else {
      items.push(1);
      if (pagina > 3) items.push("ellipsis");
      const start = Math.max(2, pagina - 1);
      const end = Math.min(total - 1, pagina + 1);
      for (let i = start; i <= end; i++) items.push(i);
      if (pagina < total - 2) items.push("ellipsis");
      items.push(total);
    }
    return items;
  }, [pagina, totalPagina]);

  // ---- Render ----
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Logs de Impressão
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Histórico de ordens, erros e conexões dos hardwares
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(isLoading || isLoadingActiveTab) && (
            <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1.5 mr-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Atualizando
            </span>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => updateMostrarTodos(!mostrarTodosHardwareDaConta)}
            title={
              mostrarTodosHardwareDaConta
                ? "Mostrar apenas meu usuário"
                : "Mostrar todos os usuários da conta"
            }
          >
            {mostrarTodosHardwareDaConta ? (
              <User className="h-4 w-4" />
            ) : (
              <Users className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {mostrarTodosHardwareDaConta ? "Apenas eu" : "Todos usuários"}
            </span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={verificarEpcsNaBase}
            title="Verificar quais EPCs já estão na base"
          >
            <Database className="h-4 w-4" />
            <span className="hidden md:inline">Conferir Base</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={exportarItensNaBase}
            title="Exportar CSV dos itens já na base"
          >
            <Download className="h-4 w-4" />
            <span className="hidden md:inline">Exportar Itens</span>
          </Button>

          <Button
            variant="default"
            size="sm"
            disabled={isLoading || isLoadingActiveTab}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            <RefreshCw
              className={`h-4 w-4 ${
                isLoading || isLoadingActiveTab ? "animate-spin" : ""
              }`}
            />
            <span className="hidden md:inline">Atualizar</span>
          </Button>
        </div>
      </div>

      {/* Stats - 3 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Total de Ordens
              </CardTitle>
              <Clipboard className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold">{totalDeOrdens}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {temFiltrosAtivos ? "Filtros aplicados" : "Todas as ordens"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Finalizadas
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-emerald-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {filtroDeErros !== "*" || filtroDeEstados !== "*"
                ? "—"
                : totalDeOrdensFinalizadas}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtroDeErros !== "*" || filtroDeEstados !== "*"
                ? "Indisponível com filtros"
                : totalDeOrdens > 0
                  ? `${Math.round(
                      (totalDeOrdensFinalizadas / totalDeOrdens) * 100,
                    )}% do total`
                  : "Nenhuma ordem"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Com Falha
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {filtroDeErros !== "*" || filtroDeEstados !== "*"
                ? "—"
                : totalDeOrdens - totalDeOrdensFinalizadas}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtroDeErros !== "*" || filtroDeEstados !== "*"
                ? "Indisponível com filtros"
                : totalDeOrdens > 0
                  ? `${Math.round(
                      ((totalDeOrdens - totalDeOrdensFinalizadas) /
                        totalDeOrdens) *
                        100,
                    )}% do total`
                  : "Nenhuma ordem"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main card */}
      <Card>
        <CardHeader className="space-y-4">
          <Tabs
            value={activeTab}
            onValueChange={(v) =>
              setActiveTab(v as "ordens" | "errors" | "conexoes")
            }
            className="w-full"
          >
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="ordens">Ordens</TabsTrigger>
              <TabsTrigger value="errors">Erros</TabsTrigger>
              <TabsTrigger value="conexoes">Conexões</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-3">
            {/* Datas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="data-inicio"
                  className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Data Início
                </label>
                <Input
                  id="data-inicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => updateDataInicio(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="hora-inicio"
                  className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Hora Início
                </label>
                <Input
                  id="hora-inicio"
                  type="time"
                  step="1"
                  disabled={!dataInicio}
                  value={horaInicio}
                  onChange={(e) => updateHoraInicio(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="data-fim"
                  className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Data Fim
                </label>
                <Input
                  id="data-fim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => updateDataFim(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="hora-fim"
                  className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Hora Fim
                </label>
                <Input
                  id="hora-fim"
                  type="time"
                  step="1"
                  disabled={!dataFim}
                  value={horaFim}
                  onChange={(e) => updateHoraFim(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            {/* Filtros por aba + per-page + limpar */}
            <div className="flex flex-col md:flex-row md:items-end gap-2">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                {activeTab === "errors" && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Tipo de Erro
                    </label>
                    <Select
                      value={filtroDeErros}
                      onValueChange={updateFiltroDeErros}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="*">Todos</SelectItem>
                          {listaDeErros.map((erro, index) => (
                            <SelectItem key={index} value={erro}>
                              {erro}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {activeTab === "ordens" && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Estado da Ordem
                    </label>
                    <Select
                      value={filtroDeEstados}
                      onValueChange={updateFiltroDeEstados}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="*">Todos</SelectItem>
                          {listaDeEstados.map((estado, index) => (
                            <SelectItem key={index} value={estado}>
                              {estado}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {activeTab === "ordens" && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="filtro-epc"
                      className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      Filtrar por EPC
                      {mostrarTodosHardwareDaConta && (
                        <span className="ml-1 text-muted-foreground/60 normal-case">
                          (indisponível no modo "Todos")
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        id="filtro-epc"
                        type="text"
                        placeholder="EPC..."
                        value={filtroEpc}
                        onChange={(e) => updateFiltroEpc(e.target.value)}
                        disabled={mostrarTodosHardwareDaConta}
                        className="h-9 pl-8 pr-8"
                      />
                      {filtroEpc && (
                        <button
                          type="button"
                          onClick={() => updateFiltroEpc("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label="Limpar EPC"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-end gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Por página
                  </label>
                  <Select
                    value={itensPorPagina}
                    onValueChange={updateItensPorPagina}
                  >
                    <SelectTrigger className="h-9 w-[110px]">
                      <SelectValue placeholder="10" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="30">30</SelectItem>
                        <SelectItem value="40">40</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="-1">Todos</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {temFiltrosAtivos && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9"
                    onClick={limparFiltros}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            {/* Info row */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground border-t pt-2">
              <span>
                Mostrando{" "}
                <strong className="text-foreground">{registrosNaTab}</strong>{" "}
                registro{registrosNaTab === 1 ? "" : "s"} nesta página
              </span>
              {activeTab === "ordens" && itensNaBaseUnicos > 0 && (
                <span className="flex items-center gap-1.5">
                  <Database className="h-3.5 w-3.5 text-blue-500" />
                  <strong className="text-foreground">
                    {itensNaBaseUnicos}
                  </strong>{" "}
                  EPC(s) na base
                </span>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-md border w-full overflow-hidden">
            <ScrollArea className="h-[450px] w-full">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    {getColumnHeadersByTab(activeTab).map((h, i) => (
                      <TableHead
                        key={i}
                        className="font-medium text-[11px] uppercase tracking-wide text-muted-foreground"
                      >
                        {h}
                      </TableHead>
                    ))}
                    {activeTab === "ordens" && (
                      <TableHead className="w-20 text-right text-[11px] uppercase tracking-wide text-muted-foreground">
                        Ações
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingActiveTab && (
                    <SkeletonRows
                      cols={
                        getColumnHeadersByTab(activeTab).length +
                        (activeTab === "ordens" ? 1 : 0)
                      }
                    />
                  )}

                  {!isLoadingActiveTab &&
                    activeTab === "errors" &&
                    (listaLogsErros.length === 0 ? (
                      <EmptyRow
                        cols={getColumnHeadersByTab(activeTab).length}
                        message="Nenhum erro encontrado"
                      />
                    ) : (
                      listaLogsErros.map((log) => (
                        <TableRow key={log._id} className="hover:bg-muted/50">
                          <TableCell className="font-medium text-xs">
                            <span className="">{log.hardware_id}</span>
                            <span className="text-muted-foreground">
                              {nomeLeitor(log.hardware_id)}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs max-w-[280px] truncate">
                            {log.descricao}
                          </TableCell>
                          <TableCell className="text-xs">
                            {log.nome_da_ordem}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                            {log.createdAt && formatarData(log.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))
                    ))}

                  {!isLoadingActiveTab &&
                    activeTab === "ordens" &&
                    (listaLogsImpressao.length === 0 ? (
                      <EmptyRow
                        cols={getColumnHeadersByTab(activeTab).length + 1}
                        message="Nenhuma ordem encontrada"
                      />
                    ) : (
                      listaLogsImpressao.map((log, index) => {
                        const variant = getStatusVariant(log.estado_da_ordem);
                        const todosConferidos = log.etiquetas_total.every(
                          (e) => listaEpcsNaBase.some((o) => o.epc === e.epc),
                        );
                        const naBaseCount = log.etiquetas_total.filter((e) =>
                          listaEpcsNaBase.some(
                            (o) => o.epc === e.epc && o.na_base,
                          ),
                        ).length;
                        const total = log.etiquetas_total.length;
                        const naBaseClass = todosConferidos
                          ? naBaseCount === total
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-red-600 dark:text-red-400"
                          : "text-muted-foreground";
                        return (
                          <TableRow
                            key={`${log.nome_da_ordem}-${index}`}
                            className="hover:bg-muted/50"
                          >
                            <TableCell className="font-medium text-xs">
                              <span className="">
                                {log.hardware_id}
                              </span>
                              <span className="text-muted-foreground">
                                {nomeLeitor(log.hardware_id)}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs font-medium">
                              {log.nome_da_ordem}
                            </TableCell>
                            <TableCell className="text-xs">
                              {log.modo_impressao ? (
                                <div className="flex flex-col gap-0.5">
                                  <span>{log.modo_impressao}</span>
                                  {log.canal_impressao && (
                                    <span className="text-[11px] text-muted-foreground">
                                      {log.canal_impressao}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                              {log.data_ultima_atualizacao &&
                                formatarData(log.data_ultima_atualizacao)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={statusBadgeClasses[variant]}
                              >
                                {getStatusText(log.estado_da_ordem)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`text-xs font-medium ${naBaseClass}`}
                              >
                                {todosConferidos
                                  ? `${naBaseCount}/${total}`
                                  : "—"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <BotaoInventarioConferencia log={log} />
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => {
                                        if (!todosConferidos) {
                                          verificarEpcsNaBase();
                                        }
                                      }}
                                    >
                                      Detalhes
                                    </Button>
                                  </DialogTrigger>
                                  <DialogModalDetalhes
                                    log={log}
                                    listaEpcsNaBase={listaEpcsNaBase}
                                    sincronizarOrdem={sincronizarOrdem}
                                    localizacoes={localizacoes}
                                    coletaZplPadrao={coletaZplPadrao}
                                  />
                                </Dialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ))}

                  {!isLoadingActiveTab &&
                    activeTab === "conexoes" &&
                    (listaLogsConexao.length === 0 ? (
                      <EmptyRow
                        cols={getColumnHeadersByTab(activeTab).length}
                        message="Nenhuma conexão encontrada"
                      />
                    ) : (
                      listaLogsConexao.map((log) => (
                        <TableRow key={log._id} className="hover:bg-muted/50">
                          <TableCell className="font-medium text-xs">
                            <span className="">{log.hardware_id}</span>
                            <span className="text-muted-foreground">
                              {nomeLeitor(log.hardware_id)}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="secondary" className="font-normal">
                              {log.tipo_da_conexao}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {log.id_conexao}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                            {log.createdAt && formatarData(log.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={statusBadgeClasses.info}
                            >
                              Ativo
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* Paginação */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 rounded-md border p-2">
            <p className="text-xs text-muted-foreground">
              Página{" "}
              <strong className="text-foreground">{pagina}</strong> de{" "}
              <strong className="text-foreground">
                {totalPagina > 0 ? totalPagina : 1}
              </strong>
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={pagina <= 1}
                onClick={() => irPara(1)}
                aria-label="Primeira página"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={pagina <= 1}
                onClick={() => irPara(pagina - 1)}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {paginationItems.map((it, i) =>
                it === "ellipsis" ? (
                  <span
                    key={`e-${i}`}
                    className="text-xs text-muted-foreground px-1"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={it}
                    variant={it === pagina ? "default" : "outline"}
                    size="sm"
                    className="h-8 min-w-[2rem] px-2 text-xs"
                    onClick={() => irPara(it)}
                  >
                    {it}
                  </Button>
                ),
              )}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={pagina >= totalPagina}
                onClick={() => irPara(pagina + 1)}
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={pagina >= totalPagina}
                onClick={() => irPara(totalPagina)}
                aria-label="Última página"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
